// =============================================================================
// src/engine/applyChanges.ts
// Given a copedant, a fret number, and a set of active control IDs, compute
// the effective MIDI pitch for every string.
//
// Key rule: pedal/lever deltas are ADDITIVE on the same string.
// e.g. if pedal A raises string 5 by +2 and pedal B raises string 5 by +1,
// engaging both gives +3 on that string.
// =============================================================================

import type { Copedant, ApplyResult, StringState } from "./types";
import { parseNoteToMidi, midiToNoteName } from "./pitch";

/**
 * Resolve every string's effective MIDI pitch at a given fret position with
 * the specified controls active.
 *
 * @param copedant        The instrument copedant definition.
 * @param fret            Fret number (0 = open / nut, 1 = first fret, etc.).
 * @param activeControls  Array of control IDs that are currently engaged.
 * @param preferSharps    Whether to display note names using sharps (default true).
 */
export function applyAtFret(
  copedant: Copedant,
  fret: number,
  activeControls: string[],
  preferSharps = true
): ApplyResult {
  const activeSet = new Set(activeControls);

  // --- Step 1: build per-string semitone delta map from all active controls ---
  // delta[stringIndex] = total semitone change from all engaged controls
  const deltaMap = new Map<number, number>();
  // Track which control IDs affected each string (for diagnostics)
  const controlsPerString = new Map<number, string[]>();

  for (const control of copedant.controls) {
    if (!activeSet.has(control.id)) continue;

    for (const change of control.changes) {
      // Additive: accumulate deltas
      const prev = deltaMap.get(change.string) ?? 0;
      const prevCtl = controlsPerString.get(change.string) ?? [];
      deltaMap.set(change.string, prev + change.delta);
      controlsPerString.set(change.string, [...prevCtl, control.id]);
    }
  }

  // --- Step 2: resolve each string ---
  const strings: StringState[] = copedant.openTuning.map((openNote, idx) => {
    const openMidi = parseNoteToMidi(openNote);
    const controlDelta = deltaMap.get(idx) ?? 0;
    // Fret raises pitch by exactly `fret` semitones above open (standard).
    const effectiveMidi = openMidi + fret + controlDelta;

    return {
      stringIndex: idx,
      openMidi,
      effectiveMidi,
      noteName: midiToNoteName(effectiveMidi, preferSharps),
      activeControls: controlsPerString.get(idx) ?? [],
    };
  });

  return { fret, activeControls, strings };
}
